import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Label } from '@shared/ui/Input';
import {
  useAnnotateDispatcherMailboxMutation,
  useForwardDispatcherManualMutation,
  useGetDispatcherEmailQuery,
  useGetDispatcherForwardQuery,
  useListMailboxRoutesQuery,
  type DispatcherForward,
} from '@shared/api';
import type { DispatcherForwardTarget } from '@entities/legal/types';

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(4)};
  background: ${({ theme }) => theme.colors.surface};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Reasoning = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.sm};
  line-height: 1.5;
`;

const ButtonsRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const InlineForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
`;

const Select = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  font-size: 14px;
`;

const CheckList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  max-height: 280px;
  overflow-y: auto;
`;

const CheckRow = styled.label<{ $muted?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  cursor: ${({ $muted }) => ($muted ? 'default' : 'pointer')};
  font-size: 13px;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.text.muted : theme.colors.text.primary)};
  transition: border-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme, $muted }) =>
      $muted ? theme.colors.border : theme.colors.borderStrong};
  }
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CheckMain = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const RouteEmail = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const SkipHint = styled.span`
  font-size: 11px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
`;

const TargetsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding-top: ${({ theme }) => theme.spacing(2)};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
`;

const TargetRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-wrap: wrap;
`;

const TargetEmail = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BlockLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 13px;
`;

// Зелёная «success»-кнопка по образцу DecisionButton $tone="good"
// в HITL классификации (DispatcherPage): outline-стиль, не заливка.
const SuccessButton = styled(Button)`
  background: transparent;
  color: ${({ theme }) => theme.colors.status.success};
  border-color: ${({ theme }) => theme.colors.status.success};
  &:hover:not(:disabled) {
    background: ${({ theme }) => `${theme.colors.status.success}22`};
    color: ${({ theme }) => theme.colors.status.success};
    transform: translateY(-1px);
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover:not(:disabled) {
      transform: none;
    }
  }
`;

interface Props {
  correspondenceId: string;
}

type Mode = 'idle' | 'annotate-wrong' | 'manual-forward';

function normalizeAddr(a: string): string {
  return a.trim().toLowerCase();
}

function statusTone(s: DispatcherForward['status']) {
  switch (s) {
    case 'sent':
    case 'manual':
      return 'success' as const;
    case 'pending_review':
    case 'pending':
      return 'warning' as const;
    case 'failed':
      return 'danger' as const;
    case 'corrected':
      return 'info' as const;
    default:
      return 'neutral' as const;
  }
}

function statusLabel(s: DispatcherForward['status']) {
  switch (s) {
    case 'sent':
      return 'Отправлено';
    case 'manual':
      return 'Отправлено вручную';
    case 'pending':
      return 'Ожидает обработки';
    case 'pending_review':
      return 'Ожидает HITL';
    case 'failed':
      return 'Ошибка отправки';
    case 'corrected':
      return 'Исправлено юристом';
    default:
      return s;
  }
}

function targetTone(s: DispatcherForwardTarget['status']) {
  switch (s) {
    case 'sent':
    case 'manual':
      return 'success' as const;
    case 'pending':
      return 'warning' as const;
    case 'failed':
      return 'danger' as const;
    case 'skipped':
      return 'neutral' as const;
    default:
      return 'neutral' as const;
  }
}

function targetLabel(t: DispatcherForwardTarget): string {
  switch (t.status) {
    case 'sent':
      return 'Отправлено';
    case 'manual':
      return 'Отправлено вручную';
    case 'pending':
      return 'В очереди';
    case 'failed':
      return 'Ошибка';
    case 'skipped':
      return t.skip_reason === 'already_in_cc' ? 'Пропущено (уже в копии)' : 'Пропущено';
    default:
      return String(t.status);
  }
}

export function MailboxForwardCard({ correspondenceId }: Props) {
  const { data, isLoading, isError } = useGetDispatcherForwardQuery(correspondenceId);
  const { data: emailData } = useGetDispatcherEmailQuery(correspondenceId);
  const { data: routesData } = useListMailboxRoutesQuery();
  const [annotate, annotateState] = useAnnotateDispatcherMailboxMutation();
  const [manual, manualState] = useForwardDispatcherManualMutation();
  const [mode, setMode] = useState<Mode>('idle');
  // annotate-wrong (одиночный выбор) — оставлен как был.
  const [correctRouteId, setCorrectRouteId] = useState('');
  const [correctEmail, setCorrectEmail] = useState('');
  const [comment, setComment] = useState('');
  // manual-forward (мультивыбор).
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [manualEmail, setManualEmail] = useState('');
  const [manualEmails, setManualEmails] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);

  const routes = useMemo(() => routesData?.items?.filter((r) => r.is_active) ?? [], [routesData]);

  // Множество адресов, уже бывших в письме (анти-петля). Источник истины —
  // backend (он дедупит по cc∪recipients∪sender∪mailer), здесь — только
  // подсказка «уже в копии — будет пропущен».
  const knownAddrs = useMemo(() => {
    const corr = emailData?.correspondence;
    const set = new Set<string>();
    if (!corr) return set;
    for (const a of corr.cc ?? []) if (a.trim()) set.add(normalizeAddr(a));
    for (const a of corr.recipients ?? []) if (a.trim()) set.add(normalizeAddr(a));
    if (corr.sender?.trim()) set.add(normalizeAddr(corr.sender));
    return set;
  }, [emailData]);

  if (isLoading) {
    return (
      <Card>
        <Title>Перенаправление на отдел</Title>
        <Row>Загрузка…</Row>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card>
        <Title>Перенаправление на отдел</Title>
        <Row>
          Этому письму ещё не назначен ящик. Автоматическая пересылка сработает после успешного
          приёма письма (или фича выключена в конфиге).
        </Row>
      </Card>
    );
  }

  const { forward, route, targets } = data;
  const reasoning = forward.llm_reasoning?.reasoning as string | undefined;

  const resetForms = () => {
    setMode('idle');
    setCorrectRouteId('');
    setCorrectEmail('');
    setComment('');
    setSelectedRouteIds([]);
    setManualEmail('');
    setManualEmails([]);
    setActionError(null);
  };

  const submitWrong = async () => {
    setActionError(null);
    try {
      await annotate({
        id: correspondenceId,
        decision: 'wrong',
        correct_route_id: correctRouteId || undefined,
        correct_email: !correctRouteId && correctEmail ? correctEmail : undefined,
        comment: comment || undefined,
      }).unwrap();
      resetForms();
    } catch (err: unknown) {
      setActionError(
        (err as { data?: { detail?: string } })?.data?.detail ??
          (err as { message?: string })?.message ??
          'Не удалось сохранить разметку.',
      );
    }
  };

  const submitCorrect = async () => {
    setActionError(null);
    try {
      await annotate({ id: correspondenceId, decision: 'correct' }).unwrap();
    } catch (err: unknown) {
      setActionError(
        (err as { data?: { detail?: string } })?.data?.detail ??
          (err as { message?: string })?.message ??
          'Не удалось сохранить разметку.',
      );
    }
  };

  const toggleRoute = (id: string) => {
    setSelectedRouteIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const addManualEmail = () => {
    const e = manualEmail.trim();
    if (!e) return;
    const norm = normalizeAddr(e);
    if (!manualEmails.some((x) => normalizeAddr(x) === norm)) {
      setManualEmails((prev) => [...prev, e]);
    }
    setManualEmail('');
  };

  const removeManualEmail = (e: string) => {
    setManualEmails((prev) => prev.filter((x) => x !== e));
  };

  const manualTargets = [
    ...selectedRouteIds.map((route_id) => ({ route_id })),
    ...manualEmails.map((email) => ({ email })),
  ];

  const submitManual = async () => {
    setActionError(null);
    try {
      await manual({
        id: correspondenceId,
        targets: manualTargets,
        comment: comment || undefined,
      }).unwrap();
      resetForms();
    } catch (err: unknown) {
      setActionError(
        (err as { data?: { detail?: string } })?.data?.detail ??
          (err as { message?: string })?.message ??
          'Не удалось отправить письмо. Проверьте SMTP-конфиг или адресата.',
      );
    }
  };

  const showManual =
    forward.status === 'pending_review' ||
    forward.status === 'failed' ||
    forward.status === 'corrected';
  const showAnnotate = forward.status === 'sent' || forward.status === 'manual';

  return (
    <Card>
      <Title>
        Перенаправление на отдел{' '}
        <Badge $tone={statusTone(forward.status)}>{statusLabel(forward.status)}</Badge>
      </Title>
      <Row>
        <span>Ящик: {route ? `${route.display_name} <${route.email}>` : '—'}</span>
        {forward.confidence !== null && (
          <span>· Уверенность: {Math.round(forward.confidence * 100)}%</span>
        )}
        {forward.sent_at && (
          <span>· Отправлено: {new Date(forward.sent_at).toLocaleString('ru')}</span>
        )}
      </Row>
      {reasoning && <Reasoning>{reasoning}</Reasoning>}
      {forward.last_error && <ErrorBox>Последняя ошибка SMTP: {forward.last_error}</ErrorBox>}

      {targets.length > 0 && (
        <TargetsBlock>
          <BlockLabel>Получатели ({targets.length})</BlockLabel>
          {targets.map((t) => (
            <TargetRow key={t.id}>
              <Badge $tone={targetTone(t.status)}>{targetLabel(t)}</Badge>
              <TargetEmail>{t.email}</TargetEmail>
              {t.last_error && <ErrorBox>· {t.last_error}</ErrorBox>}
            </TargetRow>
          ))}
        </TargetsBlock>
      )}

      {actionError && <ErrorBox>{actionError}</ErrorBox>}

      {mode === 'idle' && (
        <ButtonsRow>
          {showAnnotate && (
            <>
              <SuccessButton $size="sm" onClick={submitCorrect} disabled={annotateState.isLoading}>
                ✓ Верно перенаправил
              </SuccessButton>
              <Button
                $size="sm"
                $variant="danger"
                onClick={() => setMode('annotate-wrong')}
                disabled={annotateState.isLoading}
              >
                ✗ Не туда
              </Button>
            </>
          )}
          {showManual && (
            <Button
              $size="sm"
              $variant="primary"
              onClick={() => setMode('manual-forward')}
              disabled={manualState.isLoading}
            >
              Отправить вручную
            </Button>
          )}
        </ButtonsRow>
      )}

      {mode === 'annotate-wrong' && (
        <InlineForm>
          <Label>Правильный ящик отдела</Label>
          <Select value={correctRouteId} onChange={(e) => setCorrectRouteId(e.target.value)}>
            <option value="">— выбрать из справочника —</option>
            {routes.map((r) => (
              <option key={r.id} value={r.id}>
                {r.display_name} — {r.email}
              </option>
            ))}
          </Select>
          <Hint>Или укажите email напрямую (если ящика нет в справочнике):</Hint>
          <Input
            type="email"
            placeholder="legal@partner.ru"
            value={correctEmail}
            onChange={(e) => setCorrectEmail(e.target.value)}
            disabled={!!correctRouteId}
          />
          <Label>Комментарий (для обучения агента)</Label>
          <Input
            placeholder="Например: вложение содержит ИНН субподрядчика, всегда уходит в тендерный отдел"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <ButtonsRow>
            <Button $size="sm" $variant="ghost" onClick={resetForms}>
              Отмена
            </Button>
            <Button
              $size="sm"
              $variant="primary"
              onClick={submitWrong}
              disabled={annotateState.isLoading || (!correctRouteId && !correctEmail)}
            >
              {annotateState.isLoading ? 'Запоминаю…' : 'Запомнить и переотправить'}
            </Button>
          </ButtonsRow>
        </InlineForm>
      )}

      {mode === 'manual-forward' && (
        <InlineForm>
          <BlockLabel>Ящики отделов (можно несколько)</BlockLabel>
          {routes.length === 0 ? (
            <Hint>Активных ящиков в справочнике нет — укажите адрес вручную ниже.</Hint>
          ) : (
            <CheckList>
              {routes.map((r) => {
                const inCc = knownAddrs.has(normalizeAddr(r.email));
                return (
                  <CheckRow key={r.id} $muted={inCc}>
                    <input
                      type="checkbox"
                      checked={!inCc && selectedRouteIds.includes(r.id)}
                      onChange={() => toggleRoute(r.id)}
                      disabled={inCc}
                    />
                    <CheckMain>
                      <span>{r.display_name}</span>
                      <RouteEmail>{r.email}</RouteEmail>
                    </CheckMain>
                    {inCc && <SkipHint>уже в копии — будет пропущен</SkipHint>}
                  </CheckRow>
                );
              })}
            </CheckList>
          )}

          <BlockLabel>Адрес вручную</BlockLabel>
          <Row>
            <Input
              type="email"
              placeholder="legal@partner.ru"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addManualEmail();
                }
              }}
            />
            <Button
              $size="sm"
              $variant="ghost"
              onClick={addManualEmail}
              disabled={!manualEmail.trim()}
            >
              Добавить
            </Button>
          </Row>
          {manualEmails.length > 0 && (
            <CheckList>
              {manualEmails.map((e) => {
                const inCc = knownAddrs.has(normalizeAddr(e));
                return (
                  <CheckRow key={e} $muted={inCc} as="div">
                    <CheckMain>
                      <RouteEmail>{e}</RouteEmail>
                    </CheckMain>
                    {inCc && <SkipHint>уже в копии — будет пропущен</SkipHint>}
                    <Button $size="sm" $variant="ghost" onClick={() => removeManualEmail(e)}>
                      Убрать
                    </Button>
                  </CheckRow>
                );
              })}
            </CheckList>
          )}

          <Label>Комментарий (для обучения агента)</Label>
          <Input
            placeholder="Например: дублируем в тендерный и юридический — заявка с приложенным договором"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <ButtonsRow>
            <Button $size="sm" $variant="ghost" onClick={resetForms}>
              Отмена
            </Button>
            <Button
              $size="sm"
              $variant="primary"
              onClick={submitManual}
              disabled={manualState.isLoading || manualTargets.length === 0}
            >
              {manualState.isLoading
                ? 'Отправляю…'
                : `Переслать на выбранные (${manualTargets.length})`}
            </Button>
          </ButtonsRow>
        </InlineForm>
      )}
    </Card>
  );
}
